import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { toast } from 'react-toastify';
import {
	PencilIcon,
	ClipboardDocumentIcon,
	TrashIcon,
	PlusIcon,
} from '@heroicons/react/24/outline';

import { MainContent, PageHeading } from '../../components/Shell/Content';
import Tabs from '../../components/Tabs';
import DataTable from '../../components/DataTable';
import { DataTable as NextGenDataTable } from '../../components/nextgen';
import Modal, { DangerModal } from '../../components/Shell/Modal';
import { Button } from '../../components/Button';
import {
	TemplateField,
	TemplateDate,
	TemplateReactSelect,
	TemplateTextarea,
	TemplateBoolean,
	TemplateDropdown,
} from '../../components/Attributes/Attributes_old';
import { NOTIFY_OPTIONS } from '../../constants';
import { getUserFullName } from '../../components/helpers/functions';
import { AvatarGenerated } from '../../components/Avatar';

import useAsync from '../../hooks/useAsync';
import usePrevious from '../../hooks/usePrevious';

import {
	getOrganization,
	getOrganizationUsers,
	getOrganizationServiceReports,
	getAssetsAdmin,
	createBlankAdminServiceReport,
	updateOrganization,
	getAdminApiTokens,
	createAdminApiToken,
	revokeAdminApiToken,
	getAdminApiTokenScopes,
	getChildOrganizations,
} from '../../api/admin';
import { useApp } from '../../context/AppContextBeta';

const TYPE_OPTIONS = [
	{ label: 'Repair', value: 'repair' },
	{ label: 'Preventive Maintenance', value: 'pm' },
	{ label: 'Inspection', value: 'inspection' },
];

const DEFAULT_FORM = {
	completedOn: new Date(),
};

const Organization = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const { switchOrganization } = useApp();
	const [isCreateModalOpen, setCreateModalOpen] = useState(false);
	const [form, setForm] = useState(DEFAULT_FORM);
	const [isEditing, setIsEditing] = useState(false);
	const [editForm, setEditForm] = useState({});

	const {
		value: organization,
		loading: orgLoading,
		execute: refreshOrganization,
	} = useAsync(getOrganization, { defaultPayload: id });

	const { value: childOrganizations, loading: childOrgsLoading } = useAsync(
		getChildOrganizations,
		{ defaultPayload: id },
	);

	const { execute: onUpdateOrganization, loading: updateLoading } = useAsync(
		updateOrganization,
		{ immediate: false },
	);

	const { value: users, loading: usersLoading } = useAsync(
		getOrganizationUsers,
		{ defaultPayload: id },
	);

	const { value: serviceReports, loading: reportsLoading } = useAsync(
		getOrganizationServiceReports,
		{ defaultPayload: id },
	);

	// API Tokens
	const [isCreateTokenModalOpen, setCreateTokenModalOpen] = useState(false);
	const [tokenForm, setTokenForm] = useState({ name: '', scopes: [] });
	const [newlyCreatedToken, setNewlyCreatedToken] = useState(null);
	const [revokeTokenId, setRevokeTokenId] = useState(null);

	const {
		value: apiTokens,
		loading: tokensLoading,
		execute: refreshApiTokens,
	} = useAsync(getAdminApiTokens, { defaultPayload: id });

	const { value: availableScopes } = useAsync(getAdminApiTokenScopes);

	const { execute: onCreateApiToken, loading: createTokenLoading } = useAsync(
		createAdminApiToken,
		{ immediate: false },
	);

	const { execute: onRevokeApiToken } = useAsync(revokeAdminApiToken, {
		immediate: false,
	});

	const handleRevokeToken = (tokenId) => {
		onRevokeApiToken({ organizationId: id, tokenId }, () => {
			refreshApiTokens(id);
			setRevokeTokenId(null);
		});
	};

	const {
		value: assets,
		execute: onRefreshAssets,
		resetValue: resetAssets,
	} = useAsync(getAssetsAdmin, { immediate: false });

	const { execute: onCreateServiceReport, loading: createLoading } = useAsync(
		createBlankAdminServiceReport,
		{ immediate: false },
	);

	useEffect(() => {
		if (id) {
			onRefreshAssets(id);
		}
	}, [id]);

	const prevModalOpen = usePrevious(isCreateModalOpen);
	useEffect(() => {
		if (!isCreateModalOpen && prevModalOpen) {
			setForm(DEFAULT_FORM);
			resetAssets();
		}
	}, [isCreateModalOpen]);

	const ASSET_OPTIONS = assets?.map((asset) => ({
		label: `${asset?.manufacturer?.name || ''} ${asset?.model?.name || ''} (S/N: ${asset?.serialNumber || '-'})`,
		value: asset?.id,
		asset,
	}));

	const selectedAsset =
		ASSET_OPTIONS?.find((o) => o.value === form?.asset) || null;

	const handleOpenCreateModal = () => {
		setForm({
			...DEFAULT_FORM,
			customer: id,
			invoiceNumber: `${organization?.name?.substring(0, 3).toUpperCase()}-${Math.floor(100000 + Math.random() * 900000)}`,
		});
		setCreateModalOpen(true);
	};

	const handleCreateServiceReport = () => {
		onCreateServiceReport(
			{
				name: form?.name,
				asset: form?.asset,
				organizationId: form?.customer,
				fileName: form?.fileName,
				completedOn: form?.completedOn,
				type: form?.type,
				invoiceNumber: form?.invoiceNumber,
			},
			(payload) => {
				setCreateModalOpen(false);
				navigate(`/superadmin/service-report/${payload?.id}`);
			},
		);
	};

	return (
		<MainContent>
			<PageHeading
				onBack={() => navigate('/superadmin')}
				divider={false}
				actions={[
					{
						name: 'Create Service Report',
						onClick: handleOpenCreateModal,
					},
				]}
			>
				{organization?.name || 'Loading...'}
			</PageHeading>

			<Tabs
				tabs={[
					{ name: 'Overview', key: 'overview' },
					{ name: 'Users', key: 'users', count: users?.length || 0 },
					{
						name: 'Service Reports',
						key: 'reports',
						count: serviceReports?.length || 0,
					},
					{
						name: 'API Tokens',
						key: 'api-tokens',
						count: apiTokens?.length || 0,
					},
					{ name: 'Notifications', key: 'notifications' },
				]}
			>
				{(currentTab) => {
					if (currentTab === 'overview') {
						return (
							<>
							<div className="bg-white shadow rounded-lg">
								<div className="px-4 py-5 sm:p-6">
									<div className="flex items-center justify-between mb-4">
										<h3 className="text-lg font-medium leading-6 text-gray-900">
											Organization Details
										</h3>
										{!isEditing && (
											<button
												type="button"
												onClick={() => {
													setEditForm({
														name: organization?.name || '',
														address: organization?.address || '',
													});
													setIsEditing(true);
												}}
												className="inline-flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
											>
												<PencilIcon className="-ml-0.5 h-4 w-4" />
												Edit
											</button>
										)}
									</div>

									{isEditing ? (
										<div className="space-y-4">
											<TemplateField
												name="Name"
												value={editForm?.name}
												onChange={(index, name) =>
													setEditForm({ ...editForm, name })
												}
											/>
											<TemplateTextarea
												name="Address"
												value={editForm?.address}
												onChange={(index, address) =>
													setEditForm({ ...editForm, address })
												}
												rows={4}
												placeholder="Enter address (multiple lines supported)"
											/>
											<div className="flex justify-end gap-x-3 mt-4">
												<Button
													type="secondary"
													onClick={() => setIsEditing(false)}
												>
													Cancel
												</Button>
												<Button
													onClick={() => {
														onUpdateOrganization(
															{
																id: organization?.id,
																name: editForm?.name,
																address: editForm?.address,
															},
															() => {
																setIsEditing(false);
																refreshOrganization(organization?.id, null, {
																	overrideDefault: true,
																});
															},
														);
													}}
													disabled={updateLoading === 'pending'}
												>
													{updateLoading === 'pending' ? 'Saving...' : 'Save'}
												</Button>
											</div>
										</div>
									) : (
										<dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
											<div>
												<dt className="text-sm font-medium text-gray-500">
													Name
												</dt>
												<dd className="mt-1 text-sm text-gray-900">
													{organization?.name || '-'}
												</dd>
											</div>
											<div>
												<dt className="text-sm font-medium text-gray-500">
													Type
												</dt>
												<dd className="mt-1 text-sm text-gray-900">
													{organization?.type || '-'}
												</dd>
											</div>
											<div>
												<dt className="text-sm font-medium text-gray-500">
													Status
												</dt>
												<dd className="mt-1">
													<span
														className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
															organization?.active
																? 'bg-green-100 text-green-800'
																: 'bg-gray-100 text-gray-800'
														}`}
													>
														{organization?.active ? 'Active' : 'Inactive'}
													</span>
												</dd>
											</div>
											<div>
												<dt className="text-sm font-medium text-gray-500">
													Contract
												</dt>
												<dd className="mt-1 text-sm text-gray-900">
													{organization?.contract || '-'}
												</dd>
											</div>
											<div className="sm:col-span-2">
												<dt className="text-sm font-medium text-gray-500">
													Address
												</dt>
												<dd className="mt-1 text-sm text-gray-900 whitespace-pre-line">
													{organization?.address || '-'}
												</dd>
											</div>
										</dl>
									)}
								</div>
							</div>

							{/* Child Organizations */}
							<div className="bg-white shadow rounded-lg mt-6">
								<div className="px-4 py-5 sm:p-6">
									<h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
										Child Organizations
									</h3>
									<NextGenDataTable
										data={childOrganizations || []}
										loading={childOrgsLoading === 'pending'}
										columns={[
											{
												key: 'name',
												header: 'Name',
												render: (row) => (
													<span className="font-medium text-gray-900">
														{row.name || '-'}
													</span>
												),
											},
											{
												key: 'type',
												header: 'Type',
												render: (row) => row.type || '-',
											},
											{
												key: 'active',
												header: 'Status',
												render: (row) => (
													<span
														className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
															row.active
																? 'bg-green-100 text-green-800'
																: 'bg-gray-100 text-gray-800'
														}`}
													>
														{row.active ? 'Active' : 'Inactive'}
													</span>
												),
											},
											{
												key: 'createdAt',
												header: 'Created',
												render: (row) =>
													row.createdAt
														? moment(row.createdAt).format('MM/DD/YYYY')
														: '-',
											},
										]}
										rowActions={(row) => (
											<button
												onClick={() => {
													switchOrganization(row.id);
													navigate('/dashboard');
												}}
												className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200"
											>
												Enter Organization
											</button>
										)}
										emptyTitle="No child organizations"
										emptyDescription="This organization has no child organizations"
									/>
								</div>
							</div>
						</>
					);
					}

					if (currentTab === 'users') {
						return (
							<DataTable
								columns={[
									{
										header: '',
										accessorKey: 'avatar',
										enableSorting: false,
										cell: (info) => {
											const row = info?.row?.original;
											return (
												<AvatarGenerated
													size="xs"
													id={getUserFullName({
														firstName: row?.firstName,
														lastName: row?.lastName,
														...row,
													})}
												/>
											);
										},
									},
									{
										header: 'Name',
										accessorKey: 'name',
										cell: (info) => {
											const row = info?.row?.original;
											return getUserFullName(row) || 'Not provided';
										},
									},
									{
										header: 'Email',
										accessorKey: 'email',
										cell: (info) => info?.getValue() || '-',
									},
									{
										header: 'Role',
										accessorKey: 'role.displayName',
										cell: (info) => info?.getValue() || '-',
									},
									{
										header: 'Status',
										accessorKey: 'active',
										cell: (info) => (
											<span
												className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
													info?.getValue()
														? 'bg-green-100 text-green-800'
														: 'bg-gray-100 text-gray-800'
												}`}
											>
												{info?.getValue() ? 'Active' : 'Inactive'}
											</span>
										),
									},
								]}
								data={{ rows: users || [] }}
								usePagination={false}
								useExport={false}
								hasCheckboxes={false}
								manualSorting={false}
								loading={usersLoading === 'pending'}
							/>
						);
					}

					if (currentTab === 'reports') {
						return (
							<DataTable
								columns={[
									{
										header: 'Name',
										accessorKey: 'name',
										cell: (info) => info?.getValue() || '-',
									},
									{
										header: 'Invoice #',
										accessorKey: 'invoiceNumber',
										cell: (info) => info?.getValue() || '-',
									},
									{
										header: 'Type',
										accessorKey: 'type',
										cell: (info) => info?.getValue() || '-',
									},
									{
										header: 'Status',
										accessorKey: 'status',
										cell: (info) => info?.getValue() || '-',
									},
									{
										header: 'Completed On',
										accessorKey: 'completedOn',
										cell: (info) =>
											info?.getValue()
												? moment(info?.getValue()).format('MM/DD/YYYY')
												: '-',
									},
								]}
								data={{ rows: serviceReports || [] }}
								onRow={(row) =>
									navigate(`/superadmin/service-report/${row?.original?.id}`)
								}
								usePagination={false}
								useExport={false}
								hasCheckboxes={false}
								manualSorting={false}
								loading={reportsLoading === 'pending'}
								actions={[
									{
										label: 'Create Service Report',
										onClick: handleOpenCreateModal,
									},
								]}
							/>
						);
					}

					if (currentTab === 'api-tokens') {
						const scopesList = Array.isArray(availableScopes)
							? availableScopes
							: availableScopes?.data || [];

						const handleCreateToken = () => {
							onCreateApiToken(
								{
									organizationId: id,
									name: tokenForm.name,
									scopes: tokenForm.scopes,
								},
								(response) => {
									if (response?.data?.token) {
										setNewlyCreatedToken(response.data.token);
										setTokenForm({ name: '', scopes: [] });
										refreshApiTokens(id);
									}
								},
							);
						};

						const handleCopyToken = (token) => {
							navigator.clipboard.writeText(token);
							toast.success('Token copied to clipboard');
						};

						const handleScopeToggle = (scopeValue) => {
							const currentScopes = tokenForm.scopes || [];
							if (currentScopes.includes(scopeValue)) {
								setTokenForm({
									...tokenForm,
									scopes: currentScopes.filter((s) => s !== scopeValue),
								});
							} else {
								setTokenForm({
									...tokenForm,
									scopes: [...currentScopes, scopeValue],
								});
							}
						};

						const handleSelectAllScopes = () => {
							const allSelected = scopesList.every((s) =>
								tokenForm.scopes?.includes(s),
							);
							if (allSelected) {
								setTokenForm({ ...tokenForm, scopes: [] });
							} else {
								setTokenForm({ ...tokenForm, scopes: [...scopesList] });
							}
						};

						return (
							<div className="space-y-6">
								{/* Create Token Section */}
								<div className="bg-white shadow rounded-lg p-6">
									<h3 className="text-lg font-medium text-gray-900 mb-4">
										Create New API Token
									</h3>
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										<TemplateField
											name="Token Name"
											value={tokenForm.name}
											onChange={(_, name) =>
												setTokenForm({ ...tokenForm, name })
											}
											placeholder="e.g., Integration Token"
										/>
										<div>
											<label className="block text-xs text-gray-400 mb-2">
												Scopes
											</label>
											<div className="space-y-2">
												<label className="flex items-center gap-2 cursor-pointer">
													<input
														type="checkbox"
														checked={
															scopesList.length > 0 &&
															scopesList.every((s) =>
																tokenForm.scopes?.includes(s),
															)
														}
														onChange={handleSelectAllScopes}
														className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
													/>
													<span className="text-sm font-medium text-gray-700">
														Select All
													</span>
												</label>
												<div className="border-t border-gray-200 pt-2 space-y-2">
													{scopesList.map((scope) => (
														<label
															key={scope}
															className="flex items-center gap-2 cursor-pointer"
														>
															<input
																type="checkbox"
																checked={
																	tokenForm.scopes?.includes(scope) || false
																}
																onChange={() => handleScopeToggle(scope)}
																className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
															/>
															<span className="text-sm text-gray-600">
																{scope}
															</span>
														</label>
													))}
												</div>
											</div>
										</div>
									</div>
									<div className="mt-4">
										<Button
											onClick={handleCreateToken}
											disabled={
												!tokenForm.name ||
												!tokenForm.scopes?.length ||
												createTokenLoading === 'pending'
											}
										>
											<PlusIcon className="h-4 w-4 mr-2" />
											{createTokenLoading === 'pending'
												? 'Creating...'
												: 'Create Token'}
										</Button>
									</div>

								</div>

								{/* Existing Tokens Table */}
								<NextGenDataTable
									data={apiTokens?.data || apiTokens || []}
									loading={tokensLoading === 'pending'}
									columns={[
										{
											key: 'name',
											header: 'Name',
											render: (row) => (
												<span className="font-medium text-gray-900">
													{row.name || '-'}
												</span>
											),
										},
										{
											key: 'tokenPrefix',
											header: 'Token Prefix',
											render: (row) => (
												<code className="text-xs bg-gray-100 px-2 py-1 rounded">
													{row.tokenPrefix}...
												</code>
											),
										},
										{
											key: 'scopes',
											header: 'Scopes',
											render: (row) => (
												<div className="flex flex-wrap gap-1">
													{(row.scopes || []).map((scope) => (
														<span
															key={scope}
															className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded"
														>
															{scope}
														</span>
													))}
												</div>
											),
										},
										{
											key: 'createdAt',
											header: 'Created',
											render: (row) =>
												row.createdAt
													? moment(row.createdAt).format('MM/DD/YYYY')
													: '-',
										},
										{
											key: 'lastUsedAt',
											header: 'Last Used',
											render: (row) =>
												row.lastUsedAt
													? moment(row.lastUsedAt).format('MM/DD/YYYY HH:mm')
													: 'Never',
										},
										{
											key: 'active',
											header: 'Status',
											render: (row) => (
												<span
													className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
														row.active
															? 'bg-green-100 text-green-800'
															: 'bg-gray-100 text-gray-800'
													}`}
												>
													{row.active ? 'Active' : 'Inactive'}
												</span>
											),
										},
									]}
									rowActions={(row) => (
										<button
											onClick={() => setRevokeTokenId(row.id)}
											className="text-red-600 hover:text-red-800"
											title="Revoke token"
										>
											<TrashIcon className="h-4 w-4" />
										</button>
									)}
									emptyTitle="No API tokens"
									emptyDescription="Create your first API token above"
								/>

								{/* Token Created Modal */}
								<Modal
									title="Token Created"
									open={!!newlyCreatedToken}
									onClose={() => setNewlyCreatedToken(null)}
								>
									<div className="space-y-4">
										<div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
											<p className="text-sm text-yellow-800">
												Make sure to copy your token now. You won't be able to
												see it again!
											</p>
										</div>

										<div>
											<label className="block text-xs text-gray-400 mb-1">
												Your API Token
											</label>
											<div className="flex items-center gap-2">
												<code className="flex-1 text-sm bg-gray-100 px-3 py-2 rounded border font-mono break-all">
													{newlyCreatedToken}
												</code>
												<button
													onClick={() => handleCopyToken(newlyCreatedToken)}
													className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
													title="Copy to clipboard"
												>
													<ClipboardDocumentIcon className="h-5 w-5" />
												</button>
											</div>
										</div>

										<div className="flex justify-end pt-4">
											<Button onClick={() => setNewlyCreatedToken(null)}>
												Done
											</Button>
										</div>
									</div>
								</Modal>
							</div>
						);
					}

					if (currentTab === 'notifications') {
						const settings = organization?.settings || {};

						const handleUpdateNotificationSetting = (key, value) => {
							const updatedSettings = {
								...settings,
								[key]: value,
							};
							onUpdateOrganization(
								{
									id: organization?.id,
									settings: updatedSettings,
								},
								() => {
									refreshOrganization(organization?.id, null, {
										overrideDefault: true,
									});
								},
							);
						};

						return (
							<div className="space-y-6">
								{/* Contract Notifications */}
								<div className="bg-white shadow rounded-lg">
									<div className="px-4 py-5 sm:p-6">
										<h3 className="text-lg font-medium leading-6 text-gray-900 mb-6">
											Contract Notifications
										</h3>

										<div className="space-y-6">
											{/* Contract Expiring */}
											<div className="border-b border-gray-200 pb-6">
												<div className="flex items-start justify-between">
													<div className="flex-1">
														<TemplateBoolean
															value={
																settings.orgReminderContractsExpiringActive === null
																	? true
																	: settings.orgReminderContractsExpiringActive !== false
															}
															name="Contract Expiring Reminder"
															description="Send reminder before contract expires"
															onChange={(index, value) =>
																handleUpdateNotificationSetting(
																	'orgReminderContractsExpiringActive',
																	value,
																)
															}
														/>
													</div>
												</div>

												{settings.orgReminderContractsExpiringActive !== false && (
													<div className="mt-4 ml-12 space-y-4">
														<div>
															<label className="block text-xs text-gray-400 mb-1">
																Days before expiration
															</label>
															<TemplateDropdown
																className="w-48"
																placeholder="Select days"
																options={NOTIFY_OPTIONS}
																value={
																	settings.orgReminderContractsExpiringDays ||
																	NOTIFY_OPTIONS.find((o) => o.value === 30)?.value
																}
																onChange={(index, value) =>
																	handleUpdateNotificationSetting(
																		'orgReminderContractsExpiringDays',
																		value,
																	)
																}
															/>
														</div>
														<div>
															<label className="block text-xs text-gray-400 mb-1">
																Recipients
															</label>
															<div className="text-sm text-gray-900">
																{!settings.orgReminderContractsExpiringUsers ||
																settings.orgReminderContractsExpiringUsers?.includes('allAdmins') ? (
																	<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2">
																		All Admins
																	</span>
																) : null}
																{settings.orgReminderContractsExpiringUsers
																	?.filter((u) => u !== 'allAdmins')
																	.map((userId) => (
																		<span
																			key={userId}
																			className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mr-2"
																		>
																			User: {userId.substring(0, 8)}...
																		</span>
																	))}
																{(!settings.orgReminderContractsExpiringUsers ||
																	settings.orgReminderContractsExpiringUsers?.length === 0) && (
																	<span className="text-gray-500 text-xs">
																		Default: All Admins
																	</span>
																)}
															</div>
														</div>
													</div>
												)}
											</div>

											{/* Contract Expired */}
											<div>
												<div className="flex items-start justify-between">
													<div className="flex-1">
														<TemplateBoolean
															value={
																settings.orgReminderContractsExpiredActive === null
																	? true
																	: settings.orgReminderContractsExpiredActive !== false
															}
															name="Contract Expired Notification"
															description="Send notification on the day a contract expires"
															onChange={(index, value) =>
																handleUpdateNotificationSetting(
																	'orgReminderContractsExpiredActive',
																	value,
																)
															}
														/>
													</div>
												</div>

												{settings.orgReminderContractsExpiredActive !== false && (
													<div className="mt-4 ml-12">
														<label className="block text-xs text-gray-400 mb-1">
															Recipients
														</label>
														<div className="text-sm text-gray-900">
															{!settings.orgReminderContractsExpiredUsers ||
															settings.orgReminderContractsExpiredUsers?.includes('allAdmins') ? (
																<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2">
																	All Admins
																</span>
															) : null}
															{settings.orgReminderContractsExpiredUsers
																?.filter((u) => u !== 'allAdmins')
																.map((userId) => (
																	<span
																		key={userId}
																		className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mr-2"
																	>
																		User: {userId.substring(0, 8)}...
																	</span>
																))}
															{(!settings.orgReminderContractsExpiredUsers ||
																settings.orgReminderContractsExpiredUsers?.length === 0) && (
																<span className="text-gray-500 text-xs">
																	Default: All Admins
																</span>
															)}
														</div>
													</div>
												)}
											</div>
										</div>
									</div>
								</div>

								{/* Additional Notification Settings */}
								<div className="bg-white shadow rounded-lg">
									<div className="px-4 py-5 sm:p-6">
										<h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
											Additional Settings
										</h3>
										<dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
											<div>
												<dt className="text-sm font-medium text-gray-500">
													Pause All Notifications
												</dt>
												<dd className="mt-1">
													<span
														className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
															settings.notificationSettings?.pauseNotifications
																? 'bg-yellow-100 text-yellow-800'
																: 'bg-green-100 text-green-800'
														}`}
													>
														{settings.notificationSettings?.pauseNotifications
															? 'Paused'
															: 'Active'}
													</span>
												</dd>
											</div>
											<div>
												<dt className="text-sm font-medium text-gray-500">
													Enabled Channels
												</dt>
												<dd className="mt-1 text-sm text-gray-900">
													{settings.notificationSettings?.enabledChannels?.join(', ') ||
														'email (default)'}
												</dd>
											</div>
										</dl>
									</div>
								</div>
							</div>
						);
					}

					return null;
				}}
			</Tabs>

			<Modal
				title={`Create Service Report - ${organization?.name || ''}`}
				open={isCreateModalOpen}
				onClose={() => setCreateModalOpen(false)}
			>
				<div className="grid grid-cols-2 gap-4">
					<div className="col-span-2">
						<TemplateReactSelect
							name="Asset"
							options={ASSET_OPTIONS}
							value={selectedAsset}
							onChange={(index, asset) => {
								const reportName = `${organization?.name} ${asset?.asset?.manufacturer?.name || ''} ${asset?.asset?.model?.name || ''}`;
								setForm({
									...form,
									asset: asset?.value,
									name: reportName,
									fileName: `Scipods - ${reportName} - ${moment().format('MMDDYYYY')}.pdf`,
								});
							}}
						/>
					</div>

					<TemplateField
						name="Report Name"
						value={form?.name}
						onChange={(index, name) => setForm({ ...form, name })}
					/>

					<TemplateField
						name="File Name"
						value={form?.fileName}
						onChange={(index, fileName) => setForm({ ...form, fileName })}
					/>

					<TemplateDate
						name="Completed On"
						value={form?.completedOn}
						onChange={(index, completedOn) => setForm({ ...form, completedOn })}
					/>

					<TemplateReactSelect
						name="Type"
						options={TYPE_OPTIONS}
						value={TYPE_OPTIONS?.find((o) => o.value === form?.type) || null}
						onChange={(index, type) => setForm({ ...form, type: type?.value })}
					/>

					<TemplateField
						name="Invoice Number"
						value={form?.invoiceNumber}
						onChange={(index, invoiceNumber) =>
							setForm({ ...form, invoiceNumber })
						}
					/>

					<div className="col-span-2 flex justify-end mt-4">
						<Button
							onClick={handleCreateServiceReport}
							disabled={
								!form?.asset || !form?.name || createLoading === 'pending'
							}
						>
							{createLoading === 'pending'
								? 'Creating...'
								: 'Create Service Report'}
						</Button>
					</div>
				</div>
			</Modal>

			<DangerModal
				open={!!revokeTokenId}
				onClose={() => setRevokeTokenId(null)}
				title="Revoke API Token"
				confirmText="Revoke"
				onConfirm={() => handleRevokeToken(revokeTokenId)}
			>
				Are you sure you want to revoke this token? This action cannot be undone.
			</DangerModal>
		</MainContent>
	);
};

export default Organization;
